import { useState, useRef, useEffect, useCallback } from 'react';
import { chat, act } from '../../hooks/useAI';
import type { ChatMessage, AIChatResponse } from '../../hooks/useAI';
import AIMessage from './AIMessage';
import AIActionCard from './AIActionCard';
import AISubjectLineList from './AISubjectLineList';
import AIStreamingText from './AIStreamingText';

interface AIChatProps {
  initialMessage?: string;
  taskType?: string;
  onApplySubject?: (subject: string) => void;
  onApplyContent?: (html: string) => void;
  onActionComplete?: (result: any) => void;
  compact?: boolean;
}

interface MessageEntry {
  role: 'user' | 'assistant';
  content: string;
  provider?: string;
  model?: string;
  tokens?: number;
  actionPlan?: { action: string; params: Record<string, any>; description: string };
  subjectLines?: string[];
  isLoading?: boolean;
  isStreaming?: boolean;
  streamingKey?: number;
}

export default function AIChat({ initialMessage, taskType = 'chat', onApplySubject, onApplyContent, onActionComplete, compact }: AIChatProps) {
  const [messages, setMessages] = useState<MessageEntry[]>([]);
  const [input, setInput] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [useStream, setUseStream] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const initialized = useRef(false);

  useEffect(() => {
    if (initialMessage && !initialized.current) {
      initialized.current = true;
      handleSend(initialMessage);
    }
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const getCompletedMessages = useCallback((): MessageEntry[] => {
    return messages.filter(m => !m.isLoading && !m.isStreaming);
  }, [messages]);

  const handleSend = useCallback(async (text?: string) => {
    const messageText = text || input.trim();
    if (!messageText || isSending) return;

    const userMsg: MessageEntry = { role: 'user', content: messageText };
    const completedMessages = getCompletedMessages();
    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setIsSending(true);
    setError(null);

    const apiMessages: ChatMessage[] = [
      ...completedMessages.map((m) => ({ role: m.role, content: m.content })),
      { role: 'user' as const, content: messageText },
    ];

    if (useStream) {
      const key = Date.now();
      setMessages((prev) => [...prev, { role: 'assistant', content: '', isStreaming: true, streamingKey: key }]);
    } else {
      setMessages((prev) => [...prev, { role: 'assistant', content: '', isLoading: true }]);
      try {
        const result: AIChatResponse = await chat(apiMessages, false, taskType);
        setMessages((prev) => {
          const newMsgs = [...prev];
          const lastIdx = newMsgs.length - 1;
          if (newMsgs[lastIdx]?.isLoading) {
            newMsgs[lastIdx] = {
              role: 'assistant',
              content: result.content,
              provider: result.provider,
              model: result.model,
              tokens: result.tokens?.total_tokens,
            };
          }
          return newMsgs;
        });
      } catch (err: any) {
        setError(err.message || 'Failed to get AI response');
        setMessages((prev) => prev.slice(0, -1));
      } finally {
        setIsSending(false);
      }
    }
  }, [input, isSending, useStream, taskType, getCompletedMessages]);

  const handleStreamComplete = useCallback((fullContent: string) => {
    setMessages((prev) => {
      const newMsgs = [...prev];
      const lastIdx = newMsgs.length - 1;
      if (newMsgs[lastIdx]?.isStreaming) {
        newMsgs[lastIdx] = { ...newMsgs[lastIdx], content: fullContent, isStreaming: false };
      }
      return newMsgs;
    });
    setIsSending(false);
  }, []);

  const handleStreamError = useCallback((err: string) => {
    setError(err);
    setIsSending(false);
  }, []);

  const handleConfirmAction = useCallback(async (action: string, params: Record<string, any>) => {
    try {
      const result = await act(action, params, true);
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: result.message || 'Action completed successfully.' },
      ]);
      onActionComplete?.(result);
    } catch (err: any) {
      setError(err.message || 'Action failed');
    }
  }, [onActionComplete]);

  const handleSelectSubject = useCallback((subject: string) => {
    onApplySubject?.(subject);
  }, [onApplySubject]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const getStreamingMessages = useCallback((): ChatMessage[] => {
    return getCompletedMessages().map(m => ({ role: m.role, content: m.content }));
  }, [getCompletedMessages]);

  const renderMessageContent = (msg: MessageEntry, idx: number) => {
    if (msg.isStreaming) {
      return (
        <AIStreamingText
          key={msg.streamingKey || idx}
          messages={getStreamingMessages()}
          taskType={taskType}
          onComplete={handleStreamComplete}
          onError={handleStreamError}
        />
      );
    }

    if (msg.isLoading) {
      return (
        <div className="flex items-center gap-2 py-2">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600" />
          <span className="text-sm text-gray-500">Thinking...</span>
        </div>
      );
    }

    if (msg.actionPlan) {
      return (
        <AIActionCard
          action={msg.actionPlan.action}
          description={msg.actionPlan.description}
          params={msg.actionPlan.params}
          onConfirm={() => handleConfirmAction(msg.actionPlan!.action, msg.actionPlan!.params)}
          onCancel={() => setMessages((prev) => [...prev, { role: 'assistant', content: 'Action cancelled.' }])}
        />
      );
    }

    if (msg.subjectLines) {
      return <AISubjectLineList subjects={msg.subjectLines} onSelect={handleSelectSubject} />;
    }

    return (
      <AIMessage
        role={msg.role}
        content={msg.content}
        provider={msg.provider}
        model={msg.model}
        tokens={msg.tokens}
      />
    );
  };

  return (
    <div className={`flex flex-col ${compact ? 'h-full' : 'h-[calc(100vh-8rem)]'}`}>
      <div className="flex-1 overflow-y-auto p-4 space-y-2">
        {messages.length === 0 && (
          <div className="flex items-center justify-center h-full text-center">
            <div>
              <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900">VSENDS AI Assistant</h3>
              <p className="text-sm text-gray-500 mt-2 max-w-sm">
                Ask me to create campaigns, write emails, suggest segments, build workflows, or optimize your email marketing.
              </p>
            </div>
          </div>
        )}

        {messages.map((msg, idx) => (
          <div key={msg.streamingKey || idx}>{renderMessageContent(msg, idx)}</div>
        ))}

        {error && (
          <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
            <svg className="w-4 h-4 text-red-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-sm text-red-600 flex-1">{error}</p>
            <button
              onClick={() => { setError(null); handleSend(messages[messages.length - 1]?.content); }}
              className="text-xs text-red-600 hover:text-red-700 font-medium"
            >
              Retry
            </button>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      <div className="border-t border-gray-200 p-4 bg-white">
        <div className="flex items-end gap-2">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask VSENDS AI anything..."
            rows={1}
            className="flex-1 resize-none border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
            disabled={isSending}
          />
          <div className="flex flex-col items-center gap-1">
            <button
              onClick={() => handleSend()}
              disabled={isSending || !input.trim()}
              className="p-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
            </button>
            <button
              onClick={() => setUseStream(!useStream)}
              className={`text-xs px-1.5 py-0.5 rounded ${useStream ? 'text-blue-600 bg-blue-50' : 'text-gray-400'}`}
              title="Toggle streaming"
            >
              {useStream ? '⚡' : '○'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
